/**
 * Formulaire de confirmation d'expédition pour les commandes avec livraison
 * Permet au owner de saisir le numéro de suivi et le transporteur
 * Créé le 21 juillet 2026
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import './DeliveryTrackingForm.css';

const DeliveryTrackingForm = ({ reservationId, onSuccess, onCancel, onStartLoading, onError }) => {
  const { t } = useTranslation('delivery');
  const [formData, setFormData] = useState({
    tracking_number: '',
    carrier: '',
    estimated_delivery: '',
    carrier_name: '', // Nom du transporteur pour "autre"
    custom_tracking_url: '', // Lien de suivi personnalisé
    notes: '' // Notes optionnelles
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const carriers = [
    { value: 'colissimo', label: 'Colissimo' },
    { value: 'chronopost', label: 'Chronopost' },
    { value: 'dhl', label: 'DHL' },
    { value: 'ups', label: 'UPS' },
    { value: 'fedex', label: 'FedEx' },
    { value: 'other', label: t('carrier.other', 'Autre') }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Effacer l'erreur du champ quand l'utilisateur commence à taper
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    // Validation numéro de suivi
    if (!formData.tracking_number.trim()) {
      errors.tracking_number = t('errors.trackingRequired', 'Le numéro de suivi est obligatoire');
    } else if (formData.tracking_number.trim().length < 5) {
      errors.tracking_number = t('errors.trackingTooShort', 'Le numéro de suivi doit contenir au moins 5 caractères');
    }
    
    // Validation transporteur
    if (!formData.carrier) {
      errors.carrier = t('errors.carrierRequired', 'Le transporteur est obligatoire');
    }
    
    // Validation nom du transporteur si "autre" est sélectionné
    if (formData.carrier === 'other' && !formData.carrier_name.trim()) {
      errors.carrier_name = t('errors.carrierNameRequired', 'Le nom du transporteur est obligatoire');
    }
    
    // 🔥 Validation date de livraison estimée - OBLIGATOIRE
    if (!formData.estimated_delivery) {
      errors.estimated_delivery = t('errors.estimatedDeliveryRequired', 'La date de livraison estimée est obligatoire');
    } else {
      const selectedDate = new Date(formData.estimated_delivery);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        errors.estimated_delivery = t('errors.deliveryDatePast', 'La date de livraison doit être dans le futur');
      }
    }
    
    // Validation URL de suivi personnalisée (optionnelle mais doit être valide si renseignée)
    if (formData.custom_tracking_url && formData.custom_tracking_url.trim()) {
      const url = formData.custom_tracking_url.trim();
      
      // Vérifier que ça commence par http:// ou https://
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        errors.custom_tracking_url = t('errors.invalidTrackingUrl', 'L\'URL doit commencer par http:// ou https://');
      } 
      // Vérifier que l'URL est valide avec une regex plus stricte
      else {
        try {
          const urlObj = new URL(url);
          // Vérifier qu'il y a un domaine valide
          if (!urlObj.hostname || urlObj.hostname.length < 4 || !urlObj.hostname.includes('.')) {
            errors.custom_tracking_url = t('errors.invalidTrackingUrlFormat', 'Le format de l\'URL n\'est pas valide');
          }
        } catch (e) {
          errors.custom_tracking_url = t('errors.invalidTrackingUrlFormat', 'Le format de l\'URL n\'est pas valide');
        }
      }
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    // Valider le formulaire
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);

    try {
      // Activer l'overlay de la page parent AVANT l'appel API
      if (onStartLoading) {
        onStartLoading();
      }

      // Préparer les données à envoyer
      const dataToSend = {
        tracking_number: formData.tracking_number,
        carrier: formData.carrier,
        estimated_delivery: formData.estimated_delivery,
        custom_tracking_url: formData.custom_tracking_url.trim() || null, // Ajouter l'URL personnalisée
        notes: formData.carrier === 'other' 
          ? formData.carrier_name // Si "autre", envoyer le nom du transporteur
          : formData.notes // Sinon envoyer les notes optionnelles
      };

      const response = await api.post(
        `/delivery-orders/${reservationId}/confirm-shipment`,
        dataToSend
      );

      if (response.data.success) {
        const updatedReservation = response.data.data?.reservation || response.data.reservation;
        
        if (onSuccess) {
          onSuccess(updatedReservation);
        }
      }

    } catch (err) {
      console.error('Error confirming shipment:', err);
      
      // 🔥 Extraire l'errorKey depuis la réponse backend
      // Priorité: errorKey direct > message (si c'est une clé) > fallback
      const responseData = err.response?.data || {};
      const errorKey = responseData.errorKey 
        || responseData.message // Backend envoie l'errorKey comme message
        || 'confirmShipmentFailed';
      
      // 🔥 TOUJOURS traduire, jamais afficher le message brut
      const errorMessage = t(`errors.${errorKey}`, t('errors.confirmShipmentFailed'));
      
      console.log('🔥 [DeliveryTrackingForm] Error details:', {
        errorKey,
        errorMessage,
        translatedKey: `errors.${errorKey}`,
        response: responseData
      });
      
      setError(errorMessage);
      setLoading(false);
      
      // 🔥 Scroll vers le haut du formulaire pour montrer l'erreur
      setTimeout(() => {
        const formElement = document.querySelector('.delivery-tracking-form');
        if (formElement) {
          formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      
      // 🔥 CRITIQUE: Notifier le parent pour désactiver l'overlay
      if (onError) {
        onError(errorMessage);
      }
    }
  };

  // Vérifier si le formulaire est valide pour activer/désactiver le bouton
  const isFormValid = formData.tracking_number.trim().length >= 5 && formData.carrier && formData.estimated_delivery;

  return (
    <div className="delivery-tracking-form">
      <h3 className="form-title">
        📦 {t('confirmShipment', 'Confirmer l\'expédition')}
      </h3>

      {error && (
        <div className="error-alert">
          ⚠️ {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="tracking_number" className="required">
            {t('trackingNumber', 'Numéro de suivi')}
          </label>
          <input
            type="text"
            id="tracking_number"
            name="tracking_number"
            value={formData.tracking_number}
            onChange={handleChange}
            placeholder={t('trackingNumberPlaceholder', 'Ex: FR123456789')}
            required
            disabled={loading}
            className={`form-input ${fieldErrors.tracking_number ? 'error' : ''}`}
            autoFocus
          />
          {fieldErrors.tracking_number && (
            <small className="form-error">{fieldErrors.tracking_number}</small>
          )}
          {!fieldErrors.tracking_number && (
            <small className="form-help">
              {t('trackingNumberHelp', 'Saisissez le numéro de suivi fourni par le transporteur')}
            </small>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="carrier" className="required">
            {t('carrier', 'Transporteur')}
          </label>
          <select
            id="carrier"
            name="carrier"
            value={formData.carrier}
            onChange={handleChange}
            required
            disabled={loading}
            className={`form-select ${fieldErrors.carrier ? 'error' : ''}`}
          >
            <option value="">
              {t('selectCarrier', 'Sélectionnez un transporteur')}
            </option>
            {carriers.map(carrier => (
              <option key={carrier.value} value={carrier.value}>
                {carrier.label}
              </option>
            ))}
          </select>
          {fieldErrors.carrier && (
            <small className="form-error">{fieldErrors.carrier}</small>
          )}
        </div>

        {formData.carrier === 'other' && (
          <div className="form-group">
            <label htmlFor="carrier_name" className="required">
              {t('carrierName', 'Nom du transporteur')}
            </label>
            <input
              type="text"
              id="carrier_name"
              name="carrier_name"
              value={formData.carrier_name}
              onChange={handleChange}
              placeholder={t('carrierNamePlaceholder', 'Ex: La Poste, Mondial Relay...')}
              disabled={loading}
              className={`form-input ${fieldErrors.carrier_name ? 'error' : ''}`}
            />
            {fieldErrors.carrier_name && (
              <small className="form-error">{fieldErrors.carrier_name}</small>
            )}
          </div>
        )}

        <div className="form-group">
          <label htmlFor="estimated_delivery" className="required">
            {t('estimatedDelivery', 'Date de livraison estimée')}
          </label>
          <input
            type="date"
            id="estimated_delivery"
            name="estimated_delivery"
            value={formData.estimated_delivery}
            onChange={handleChange}
            min={new Date().toISOString().split('T')[0]}
            disabled={loading}
            className={`form-input ${fieldErrors.estimated_delivery ? 'error' : ''}`}
          />
          {fieldErrors.estimated_delivery && (
            <small className="form-error">{fieldErrors.estimated_delivery}</small>
          )}
          {!fieldErrors.estimated_delivery && (
            <small className="form-help">
              {t('estimatedDeliveryHelp', 'Date prévue de livraison au client')}
            </small>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="custom_tracking_url">
            {t('customTrackingUrl', 'Lien de suivi personnalisé')} {t('common.optional', '(optionnel)')}
          </label>
          <input
            type="text"
            id="custom_tracking_url"
            name="custom_tracking_url"
            value={formData.custom_tracking_url}
            onChange={handleChange}
            placeholder={t('customTrackingUrlPlaceholder', 'https://...')}
            disabled={loading}
            className={`form-input ${fieldErrors.custom_tracking_url ? 'error' : ''}`}
          />
          {fieldErrors.custom_tracking_url && (
            <small className="form-error">{fieldErrors.custom_tracking_url}</small>
          )}
          {!fieldErrors.custom_tracking_url && (
            <small className="form-help">
              {t('customTrackingUrlHelp', 'URL de suivi spécifique de votre transporteur')}
            </small>
          )}
        </div>

        {formData.carrier !== 'other' && (
          <div className="form-group">
            <label htmlFor="notes">
              {t('notes', 'Notes optionnelles')}
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder={t('notesPlaceholder', 'Informations complémentaires sur l\'expédition...')}
              rows="3"
              disabled={loading}
              className="form-textarea"
            />
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="btn btn-secondary"
          >
            {t('common.cancel', 'Annuler')}
          </button>
          <button
            type="submit"
            disabled={loading || !isFormValid}
            className="btn btn-primary"
            title={!isFormValid ? t('errors.fillRequired', 'Veuillez remplir tous les champs obligatoires') : ''}
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                {t('confirming', 'Confirmation en cours...')}
              </>
            ) : (
              <>
                ✅ {t('confirmShipmentButton', 'Confirmer l\'expédition')}
              </>
            )}
          </button>
        </div>
      </form>

      <div className="info-box">
        <strong>ℹ️ {t('infoTitle', 'Information importante')}</strong>
        <p>
          {t('infoMessage', 
            'Une fois l\'expédition confirmée, le client recevra un email avec le numéro de suivi. Le paiement sera disponible dans 21 jours pour vous laisser le temps de gérer d\'éventuels problèmes de livraison.'
          )}
        </p>
      </div>
    </div>
  );
};

export default DeliveryTrackingForm;
